import React, { useState, useEffect, useId, useRef } from 'react'
import Select from 'react-select'
import useStore from 'state/knovStore'
import UserIcon from 'components/users/UserIcon'
import UserEditForm from './UserEditForm'
import CableApp from '../../actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import api from '../../api/api'
import { clearAccessTokens } from 'api/accessTokenMgmt'
import { redirectToLoginScreen } from '../../AppRoot'
import cc from 'cryptocompare'
import cn from 'classnames'
import styles from './profile.module.scss'
import WithTooltip from 'components/shared/WithTooltip'
import Button, { PurpleButton } from 'components/shared/buttons/Button'
import { isMobile } from 'lib/device'
import UserImageUpdate from 'components/modals/UserImageUpdate'
import useUserBannerUpload from 'components/users/useUserBannerUpload'
import queryClient from 'api/queryClient'
import { cacheUser } from 'state/cache'
import { LocalCurrencyTabs } from 'components/votes/LocalCurrencyTabs'
import { themedSettingsSelectStyles } from 'components/shared/reactSelectStyles'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import FeatureFlagSettings from 'components/users/FeatureFlagSettings'
import DeleteAccountConfirmMessage from 'components/users/DeleteAccountConfirmMessage'

const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English (English)' },
    { value: 'zh', label: 'Chinese (中文)' },
    { value: 'hi', label: 'Hindi (हिन्दी)' },
    { value: 'es', label: 'Spanish (Español)' },
    { value: 'fr', label: 'French (Français)' },
    { value: 'ar', label: 'Arabic (العربية)' },
    { value: 'bn', label: 'Bengali (বাংলা)' },
    { value: 'pt', label: 'Portuguese (Português)' },
    { value: 'ru', label: 'Russian (Русский)' },
    { value: 'ja', label: 'Japanese (日本語)' },
    { value: 'de', label: 'German (Deutsch)' },
    { value: 'jv', label: 'Javanese (Basa Jawa)' },
    { value: 'ko', label: 'Korean (한국어)' },
    { value: 'tr', label: 'Turkish (Türkçe)' },
    { value: 'vi', label: 'Vietnamese (Tiếng Việt)' },
    { value: 'ta', label: 'Tamil (தமிழ்)' },
    { value: 'ur', label: 'Urdu (اردو)' },
    { value: 'it', label: 'Italian (Italiano)' },
    { value: 'fa', label: 'Persian (فارسی)' },
    { value: 'pl', label: 'Polish (Polski)' },
    { value: 'sw', label: 'Swahili (Kiswahili)' },
]

const DELETE_ACCOUNT_MODAL_KEY = 'delete-account'

const findLanguageOption = value =>
    LANGUAGE_OPTIONS.find(option => option.value === value) || LANGUAGE_OPTIONS[0]

export default function EditProfile(props) {
    const [user, setUser] = useState(window.gon.currentUser)
    const [balance, setBalance] = useState(null)
    const [potentialBsv, setPotentialBsv] = useState(props.potentialBsv)
    const [potentialUsd, setPotentialUsd] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [updatedAt, setUpdatedAt] = useState(new Date().getTime())
    const [mutedUsers, setMutedUsers] = useState(gon?.currentUser?.muted_users)
    const [showImageUpdate, setShowImageUpdate] = useState(false)
    const [isDeletingAccount, setIsDeletingAccount] = useState(false)
    const deleteAccountRef = useRef(null)
    const deletePasswordRef = useRef('')
    const [selectedLanguage, setSelectedLanguage] = useState(
        user?.options?.preferredLanguage || 'en',
    )
    const languageSelectId = useId()
    const set = useStore(state => state.set)
    const openConfirmModal = useStore(state => state.actions.modalActions.openConfirmModal)
    const selectMenuPortalTarget = typeof document === 'undefined' ? undefined : document.body

    // Twitter OAuth 2.0 connection handler
    const handleTwitterConnect = async () => {
        try {
            // OAuth 2.0 flow - redirect directly to Twitter
            const clientId = window.gon?.twitterClientId
            if (!clientId) {
                console.error('Twitter Client ID not found')
                return
            }

            const redirectUri = encodeURIComponent(
                window.gon?.twitterRedirectUri || 'https://bore.nikv.me/auth/twitter/callback',
            )

            // Generate proper PKCE parameters
            const codeVerifier = generateCodeVerifier()
            const codeChallenge = await generateCodeChallenge(codeVerifier)

            // Get encrypted state from backend - it contains user info and code_verifier
            const response = await api.storeTwitterPKCE(codeVerifier, null)
            const state = response.state

            // Construct Twitter OAuth 2.0 authorization URL with proper PKCE
            // CRITICAL: Include offline.access scope to get refresh tokens
            const twitterAuthUrl =
                `https://twitter.com/i/oauth2/authorize?` +
                `response_type=code&` +
                `client_id=${clientId}&` +
                `redirect_uri=${redirectUri}&` +
                `scope=tweet.read%20tweet.write%20users.read%20media.write%20offline.access&` +
                `state=${state}&` +
                `code_challenge=${codeChallenge}&` +
                `code_challenge_method=S256`

            // Redirect to Twitter for authorization
            window.location.href = twitterAuthUrl
        } catch (error) {
            console.error('Error during Twitter OAuth setup:', error)
            console.error('Error details:', error.message, error.stack)
            alert('Failed to connect to Twitter/X. Please try again.')
        }
    }

    // Generate code verifier for PKCE
    const generateCodeVerifier = () => {
        const array = new Uint8Array(32)
        crypto.getRandomValues(array)
        return base64UrlEncode(array)
    }

    // Generate code challenge from verifier
    const generateCodeChallenge = async verifier => {
        const encoder = new TextEncoder()
        const data = encoder.encode(verifier)
        const digest = await crypto.subtle.digest('SHA-256', data)
        return base64UrlEncode(new Uint8Array(digest))
    }

    // Base64 URL encoding (no padding, URL-safe characters)
    const base64UrlEncode = buffer => {
        const base64 = btoa(String.fromCharCode.apply(null, buffer))
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    }

    useEffect(function getUserSpaces() {
        api.getSpaces().then(spaces => {
            set({ userSpaces: spaces })
        })
    }, [])

    const toggleEmailNotifications = async e => {
        const email_noti = e.target.checked
        const data = await api.updateUser(user.id, { email_noti })

        if (data.user) {
            setUser(data.user)
            try {
                queryClient.setQueryData(['user', String(user.id)], data.user)
            } catch (e) {}
            try {
                cacheUser(data.user, true)
            } catch (e) {}
            try {
                window.gon.currentUser = data.user
            } catch (e) {}
        } else {
            console.error('Error updating email notification settings:', data.errors)
        }
    }

    useEffect(() => {
        if (user && potentialBsv) {
            const getUsd = async () => {
                const price = await cc.price('bsv', 'usd')
                if (price && price['USD']) {
                    const bsv = (potentialBsv * price['USD']).toFixed(2)
                    setPotentialUsd(bsv)
                } else {
                    setPotentialUsd(0)
                }
            }
            getUsd()
        }
    }, [potentialBsv])

    useEffect(() => {
        if (user) {
            CableApp.channel = CableApp.cable.subscriptions.create(
                withChannelAuth({ channel: 'UserChannel', userId: user.id }),
                {
                    received: async data => {
                        if (data.message === 'balance updated') {
                            setBalance(data.balance)
                            setIsLoading(false)
                            if (data.hasOwnProperty('potentialBsv')) {
                                setPotentialBsv(data.potentialBsv)
                            }
                        }
                    },
                    // @ts-expect-error requestBalance is on api at runtime but not in the exported type
                    connected: api.requestBalance,
                },
            )
        }
        return () => {
            if (CableApp.channel && CableApp.channel.unsubscribe) {
                CableApp.channel.unsubscribe()
            }
        }
    }, [])

    const showHandcash =
        gon.currentUser &&
        gon.currentUser.features &&
        gon.currentUser.features.some(feature => feature.name === 'handcash')

    const handleImageUpdateComplete = newUser => {
        setUpdatedAt(new Date().getTime())
        setUser(newUser)
        setShowImageUpdate(false)
        try {
            queryClient.setQueryData(['user', String(newUser.id)], newUser)
        } catch (e) {}
        try {
            cacheUser(newUser, true)
        } catch (e) {}
        try {
            window.gon.currentUser = newUser
        } catch (e) {}

        // Dispatch event to update all UserIcon instances
        window.dispatchEvent(new CustomEvent('userAvatarUpdated', { detail: { user: newUser } }))
    }

    const handleBannerUpdateComplete = newUser => {
        setBannerError(false)
        setUser(newUser)
        try {
            queryClient.setQueryData(['user', String(newUser.id)], newUser)
        } catch (e) {}
        try {
            cacheUser(newUser, true)
        } catch (e) {}
        try {
            window.gon.currentUser = newUser
        } catch (e) {}
    }

    const [bannerError, setBannerError] = useState(false)
    const { openBannerPicker, bannerUploadUi } = useUserBannerUpload({
        user,
        onUpdated: handleBannerUpdateComplete,
    })

    const toggleUserFeature = async featureName => {
        const response = await api.toggleUserFeature(featureName)
        if (response.features) {
            const normalizedFeatures = response.features.map(name => ({ name }))
            const newFeatures = {
                ...user,
                features: normalizedFeatures,
            }
            setUser(newFeatures)
            window.gon.currentUser = {
                ...window.gon.currentUser,
                features: normalizedFeatures,
            }
            set(state => {
                state.userFeatures = normalizedFeatures
            })

            const newGon = await api.getGon()
            if (newGon?.currentUser) {
                // update the global gon to make sure the rest of the app knows about it
                window.gon = newGon
            }
        }
        return response
    }

    const toggleEnterToPost = () => toggleUserFeature('enter_key_posts_immediately')

    const hasFeature = featureName => user.features?.some(f => f.name === featureName)

    const handleLanguageChange = async option => {
        if (!option) return
        const lang = option.value
        setSelectedLanguage(lang)
        try {
            await api.updateUserOptions({ preferredLanguage: lang })
            window.gon.currentUser.options.preferredLanguage = lang // make sure to update gon for current session
        } catch (error) {
            console.error('Error updating language:', error)
        }
    }

    // /delete-account (the public deletion link) lands here: bring the card into view.
    useEffect(() => {
        if (props.focusDeleteAccount) {
            deleteAccountRef.current?.scrollIntoView({ block: 'center' })
        }
    }, [])

    // Opened as a modal, settings shares the single modal slot with the confirm
    // dialog, which replaces it. Reopen it when the user backs out or deletion fails.
    const returnToSettings = () => {
        if (props.modalParams) {
            set({ isModalOpen: true, modalParams: props.modalParams })
        }
    }

    const deleteAccount = async () => {
        setIsDeletingAccount(true)
        try {
            const response = await api.deleteAccount(deletePasswordRef.current)
            if (!response?.success) {
                throw new Error(response?.error || 'Account deletion failed')
            }
            window.logEv?.('DELETE_ACCOUNT', {
                location: props.focusDeleteAccount ? 'delete_account_link' : 'settings',
            })
            // The account is gone server-side; leave the app the same way logout does.
            clearAccessTokens()
            // @ts-expect-error savePath inferred as literal true due to default value shape
            redirectToLoginScreen({ savePath: false })
        } catch (error) {
            console.error('Error deleting account:', error)
            setIsDeletingAccount(false)
            if (error?.message === 'current_password_required') {
                openConfirmModal(
                    'Enter your current password to delete your account.',
                    'OK',
                    returnToSettings,
                    { hideCancel: true, iconType: 'error', onDismiss: returnToSettings },
                )
                return
            }
            if (error?.message === 'wallet_backup_required') {
                // The server holds wallet keys this page didn't know about yet.
                openDeleteAccountConfirm('server')
                return
            }
            // A modal rather than inline text: in the settings modal this component
            // has already been replaced by the confirm dialog.
            openConfirmModal(
                'Could not delete your account. Please try again.',
                'OK',
                returnToSettings,
                { hideCancel: true, iconType: 'error', onDismiss: returnToSettings },
            )
        }
    }

    // The confirm stays disabled until the wallet key file is saved, when
    // Treechat holds keys for the account (the server enforces it too).
    const enableDeleteAccountConfirm = () => {
        const { modalParams } = useStore.getState()
        if (modalParams?.modalKey !== DELETE_ACCOUNT_MODAL_KEY) return
        set({ modalParams: { ...modalParams, confirmDisabled: false } })
    }

    const openDeleteAccountConfirm = walletBackupSource => {
        deletePasswordRef.current = ''
        openConfirmModal(
            <DeleteAccountConfirmMessage
                walletBackupSource={walletBackupSource}
                userId={user.id}
                onWalletBackedUp={enableDeleteAccountConfirm}
                passwordRef={user?.delete_requires_password ? deletePasswordRef : undefined}
            />,
            'Delete Account',
            deleteAccount,
            {
                modalKey: DELETE_ACCOUNT_MODAL_KEY,
                headerTitle: 'Delete your account?',
                iconType: 'warning',
                confirmVariant: 'danger',
                confirmDisabled: Boolean(walletBackupSource),
                // Enter on the download button must not also delete the account.
                confirmOnEnter: false,
                onCancel: returnToSettings,
            },
        )
    }

    const confirmDeleteAccount = () => {
        if (isDeletingAccount) return
        openDeleteAccountConfirm(user?.wallet_backup_source)
    }

    return (
        <div className={styles.profileContainer}>
            <div
                className={cn(
                    styles.profileComp,
                    isMobile && styles.mobileProfileComp,
                    props.modalParams && styles.modalProfileComp,
                )}
            >
                <div className={styles.userAvatarForm}>
                    <div className={styles.header}>
                        <div className={styles.userLogo} onClick={() => setShowImageUpdate(true)}>
                            <UserIcon
                                user={user}
                                updatedAt={updatedAt}
                                contextStyles={styles.userIcon}
                                size={'large'}
                            />
                            <div className={styles.editIconOverlay}>
                                <i className="fa fa-pencil" />
                            </div>
                        </div>
                        <div className={styles.userName}> {user.name} </div>
                    </div>
                </div>

                {showImageUpdate && (
                    <div className={styles.imageUpdateOverlay}>
                        <div className={styles.imageUpdateContainer}>
                            <div className={styles.imageUpdateHeader}>
                                <h3>Update Profile Picture</h3>
                                <button
                                    className={styles.closeButton}
                                    onClick={() => setShowImageUpdate(false)}
                                >
                                    <i className="fa fa-remove" />
                                </button>
                            </div>
                            <div>
                                <UserImageUpdate
                                    modalParams={{
                                        user,
                                        callback: handleImageUpdateComplete,
                                    }}
                                    closeModal={() => setShowImageUpdate(false)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={cn(styles.bannerSection, styles.sectionCard)}>
                    <div className={styles.sectionHeader}>Profile Banner</div>
                    <div className={styles.bannerPreview} onClick={openBannerPicker}>
                        {user.banner_url && !bannerError ? (
                            <img
                                src={`${window.knovApiUrl || ''}${user.banner_url}`}
                                className={styles.bannerImage}
                                onError={() => setBannerError(true)}
                                alt=""
                            />
                        ) : (
                            <div className={styles.bannerPlaceholder}>
                                <span>Click to upload banner</span>
                            </div>
                        )}
                        <div className={styles.editBannerButton}>
                            <i className="fa fa-pencil" />
                        </div>
                    </div>
                    {bannerUploadUi}
                </div>

                {showHandcash && (
                    <>
                        {!props.withHandcash && potentialBsv > 0 && (
                            <div className={cn(styles.integrationContainer, styles.sectionCard)}>
                                <div>{`Integrate Handcash to claim $${potentialUsd} of upvalue.`}</div>
                            </div>
                        )}

                        <div className={cn(styles.integrationContainer, styles.sectionCard)}>
                            Your Handcash upvalue balance:{' '}
                            {isLoading ? <i className={'fa fa-spin fa-spinner'} /> : `$${balance}`}
                        </div>

                        <div className={cn(styles.integrationContainer, styles.sectionCard)}>
                            <a id="connectButton" href={props.handcashRegistrationUrl}>
                                Connect with Handcash
                            </a>
                        </div>
                    </>
                )}

                <div className={cn(styles.editUserFormContainer, styles.sectionCard)}>
                    <div className={styles.sectionHeader}>Profile Information</div>
                    <div>
                        <UserEditForm user={user} setUser={setUser} />
                    </div>
                </div>

                <div className={cn(styles.notiContainer, styles.sectionCard)}>
                    <div className={styles.sectionHeader}>Upvalue Settings</div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>Choose Currency</span>
                            <div className={styles.emailNotiDescription}>
                                Select whether to display amounts in Bitcoin or USD
                            </div>
                        </div>
                        <div style={{ marginTop: '10px' }}>
                            <LocalCurrencyTabs
                                bsvUnit={gon?.currentUser?.space_options?.bsvUnit || 'bitcoin'}
                                onChange={async newUnit => {
                                    useStore.setState({ bsvUnit: newUnit })
                                    await api.updateUserSpaceOptions({
                                        bsvUnit: newUnit,
                                    })
                                    setUser({ ...window.gon.currentUser })
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>Show Upvalue FX</span>
                            <div className={styles.emailNotiDescription}>
                                Enable or disable the visual effects when upvaluing
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                checked={gon?.currentUser?.space_options?.effectEnabled !== false}
                                onChange={async e => {
                                    const enabled = e.target.checked
                                    useStore.setState({ effectEnabled: enabled })
                                    await api.updateUserSpaceOptions({
                                        effectEnabled: enabled,
                                    })
                                    setUser({ ...window.gon.currentUser })
                                }}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>
                                Show Balance After Upvalue
                            </span>
                            <div className={styles.emailNotiDescription}>
                                Briefly show your remaining balance after you upvalue
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                data-testid="upvalue-balance-toast-toggle"
                                checked={
                                    gon?.currentUser?.space_options?.upvalueBalanceToast === true
                                }
                                onChange={async e => {
                                    await api.updateUserSpaceOptions({
                                        upvalueBalanceToast: e.target.checked,
                                    })
                                    setUser({ ...window.gon.currentUser })
                                }}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                </div>

                <div className={cn(styles.notiContainer, styles.sectionCard)}>
                    <div className={styles.sectionHeader}>Posting Settings</div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>Enter to Post</span>
                            <div className={styles.emailNotiDescription}>
                                Post messages by pressing Enter.
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                checked={hasFeature('enter_key_posts_immediately')}
                                onChange={toggleEnterToPost}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>
                                Confirm Before Posting Publicly
                            </span>
                            <div className={styles.emailNotiDescription}>
                                Ask for confirmation before posting publicly
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                checked={hasFeature('confirm_before_posting_publicly')}
                                onChange={() =>
                                    toggleUserFeature('confirm_before_posting_publicly')
                                }
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                </div>

                <div className={cn(styles.notiContainer, styles.sectionCard)}>
                    <div className={styles.sectionHeader}>Notification Settings</div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>Email Notifications</span>
                            <div className={styles.emailNotiDescription}>
                                Receive notification emails from Treechat
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                aria-label="Email Notifications"
                                checked={user.email_noti !== false}
                                onChange={toggleEmailNotifications}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>

                    <div className={styles.emailNotiContainer}>
                        <div className={styles.emailNoti}>
                            <span className={styles.emailNotiLabel}>
                                AI Image & Video Notifications
                            </span>
                            <div className={styles.emailNotiDescription}>
                                Get notified when your AI image or video generation is complete
                            </div>
                        </div>

                        <label className={styles.toggleSwitch}>
                            <input
                                type="checkbox"
                                checked={!hasFeature('suppress_ai_generation_notifications')}
                                onChange={() =>
                                    toggleUserFeature('suppress_ai_generation_notifications')
                                }
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                </div>

                {hasFeature('feature_ui') && (
                    <div className={cn(styles.notiContainer, styles.sectionCard)}>
                        <div className={styles.sectionHeader}>Experimental Settings</div>
                        <FeatureFlagSettings onToggle={toggleUserFeature} />
                    </div>
                )}

                {user.features?.some(f => f.name === 'layout_setting') && (
                    <div className={cn(styles.notiContainer, styles.sectionCard)}>
                        <div className={styles.sectionHeader}>Layout Settings</div>

                        <div className={styles.emailNotiContainer}>
                            <div className={styles.emailNoti}>
                                <span className={styles.emailNotiLabel}>Full Width Messages</span>
                                <div className={styles.emailNotiDescription}>
                                    Display all messages with full width layout and consistent
                                    indentation.
                                </div>
                            </div>

                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={gon?.currentUser?.space_options?.layout === 'indent'}
                                    onChange={async () => {
                                        const currentSpaceOptions =
                                            gon?.currentUser?.space_options || {}
                                        const newLayout =
                                            currentSpaceOptions.layout === 'indent'
                                                ? 'indent'
                                                : null
                                        const response = /** @type {{user?: any}} */ (
                                            await api.updateUserSpaceOptions({
                                                ...currentSpaceOptions,
                                                layout: newLayout,
                                            })
                                        )
                                        if (response?.user) {
                                            // Update both local user state and global gon
                                            setUser(response.user)
                                            window.gon.currentUser = response.user

                                            // Show confirmation modal using store
                                            set(state => {
                                                state.isModalOpen = true
                                                state.modalParams = {
                                                    type: 'Confirm',
                                                    title: 'Reload Required',
                                                    message:
                                                        'The layout changes require a page reload to take effect. Would you like to reload now?',
                                                    confirmText: 'Reload Now',
                                                    cancelText: 'Later',
                                                    onConfirm: () => {
                                                        window.location.reload()
                                                    },
                                                }
                                            })
                                        }
                                    }}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                    </div>
                )}

                {mutedUsers && mutedUsers.length > 0 && (
                    <div className={cn(styles.muteListContainer, styles.sectionCard)}>
                        <div className={styles.sectionHeader}>Muted Users</div>

                        <div className={styles.muteList}>
                            {user?.muted_users.map(mutedUser => {
                                return (
                                    <div className={styles.mutedUserContainer}>
                                        <div className={styles.mutedUser}>
                                            <div className={styles.mutedUserIcon}>
                                                <UserIcon
                                                    user={mutedUser}
                                                    contextStyles={styles.mutedUserIcon}
                                                    size={'medium'}
                                                />
                                            </div>

                                            <div className={styles.mutedUserName}>
                                                {mutedUser.name}
                                            </div>
                                        </div>

                                        <div className={styles.mutedUserButtonContainer}>
                                            <PurpleButton
                                                text="Unmute"
                                                onClick={async () => {
                                                    await api.unmuteUser(mutedUser.id)
                                                    setMutedUsers(
                                                        mutedUsers.filter(
                                                            user => user.id !== mutedUser.id,
                                                        ),
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                <div
                    className={cn(styles.languageSelectorContainer, styles.sectionCard)}
                    {...panelSwipeExclusionProps}
                >
                    <div className={styles.sectionHeader}>Language</div>
                    <label className={styles.languageSelectorLabel} htmlFor={languageSelectId}>
                        Preferred Language
                    </label>
                    <div className={styles.languageSelectorDropdown}>
                        <Select
                            inputId={languageSelectId}
                            value={findLanguageOption(selectedLanguage)}
                            onChange={handleLanguageChange}
                            options={LANGUAGE_OPTIONS}
                            styles={themedSettingsSelectStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            isSearchable={false}
                            isClearable={false}
                        />
                    </div>
                </div>

                {/* Twitter Integration Section */}
                <div
                    className={cn(styles.integrationContainer, styles.sectionCard, {
                        [styles.mobileIntegrationContainer]: isMobile,
                    })}
                >
                    {/* Mobile overlay to intercept all clicks */}
                    {isMobile && (
                        <div
                            className={styles.mobileOverlay}
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                openConfirmModal(
                                    'Twitter/X integration is only available on desktop browsers. Please use a desktop or laptop computer to connect your Twitter/X account.',
                                    'OK',
                                    () => {},
                                    {
                                        hideCancel: true,
                                        iconType: 'warning',
                                    },
                                )
                            }}
                            onMouseDown={e => {
                                e.preventDefault()
                                e.stopPropagation()
                            }}
                            onTouchStart={e => {
                                e.stopPropagation()
                            }}
                        />
                    )}
                    <div className={styles.sectionHeader}>Twitter/X Integration (beta)</div>
                    <div className={styles.integrationDescription}>
                        Connect your Twitter account to post to Twitter/X directly from Treechat
                    </div>
                    <div className={styles.twitterConnectionContainer}>
                        {user.twitter_providers && user.twitter_providers.length > 0 ? (
                            <div className={styles.twitterConnectedContainer}>
                                <div className={styles.twitterConnectedInfo}>
                                    <div className={styles.twitterIconWrapper}>
                                        <i className="fa fa-twitter"></i>
                                    </div>
                                    <div className={styles.twitterAccountDetails}>
                                        <span className={styles.twitterUsername}>
                                            @{user.twitter_providers[0].username}
                                        </span>
                                        <span
                                            className={cn(styles.twitterConnectedLabel, {
                                                [styles.clickable]:
                                                    user.options
                                                        ?.twitter_auth_needs_media_scope_update,
                                            })}
                                            onClick={
                                                user.options?.twitter_auth_needs_media_scope_update
                                                    ? e => {
                                                          e.preventDefault()
                                                          e.stopPropagation()
                                                          set({
                                                              isModalOpen: true,
                                                              modalParams: {
                                                                  type: 'TwitterMediaAlert',
                                                              },
                                                          })
                                                      }
                                                    : undefined
                                            }
                                            title={
                                                user.options?.twitter_auth_needs_media_scope_update
                                                    ? 'Twitter/X account needs reconnection for media posting'
                                                    : undefined
                                            }
                                        >
                                            {user.options?.twitter_auth_needs_media_scope_update ? (
                                                <div className={styles.twitterWarningIcon}>
                                                    <i className="fa fa-exclamation"></i>
                                                </div>
                                            ) : (
                                                <i className="fa fa-check-circle"></i>
                                            )}
                                            {user.options?.twitter_auth_needs_media_scope_update
                                                ? 'Needs Update'
                                                : 'Connected'}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.twitterConnectedButtonContainer}>
                                    <button
                                        className={cn(styles.twitterDisconnectButton, {
                                            [styles.disabled]: isMobile,
                                        })}
                                        disabled={isMobile}
                                        onClick={async e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            if (isMobile) return
                                            if (
                                                window.confirm(
                                                    'Are you sure you want to disconnect your Twitter/X account?',
                                                )
                                            ) {
                                                try {
                                                    await api.deleteProvider(
                                                        user.twitter_providers[0].id,
                                                    )
                                                    // Refresh user data
                                                    const newGon = await api.getGon()
                                                    if (newGon?.currentUser) {
                                                        window.gon = newGon
                                                        setUser(newGon.currentUser)
                                                    }
                                                } catch (error) {
                                                    console.error(
                                                        'Error disconnecting Twitter:',
                                                        error,
                                                    )
                                                }
                                            }
                                        }}
                                    >
                                        Disconnect
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                className={cn(styles.twitterConnectButton, {
                                    [styles.disabled]: isMobile,
                                })}
                                disabled={isMobile}
                                onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (!isMobile) {
                                        handleTwitterConnect()
                                    }
                                }}
                            >
                                <i className="fa fa-twitter"></i>
                                Connect Twitter/X Account
                            </button>
                        )}
                    </div>
                </div>

                <div
                    ref={deleteAccountRef}
                    className={cn(
                        styles.deleteAccountContainer,
                        styles.sectionCard,
                        props.focusDeleteAccount && styles.deleteAccountFocused,
                    )}
                >
                    <div className={styles.sectionHeader}>Delete Account</div>
                    <div className={styles.integrationDescription}>
                        Removes your profile, posts and threads from Treechat, then erases your
                        account data after 30 days. This cannot be undone.
                    </div>
                    <div className={styles.deleteAccountActions}>
                        <Button
                            type="default"
                            icon="fa fa-trash-o"
                            text="Delete Account"
                            inProgress={isDeletingAccount}
                            contextStyles={styles.deleteAccountButton}
                            onClick={confirmDeleteAccount}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
